import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Trash2, CheckSquare, AlertTriangle, Package } from 'lucide-react';
import { memberApi, inputCls } from './memberApi';

const toDateInput = (d) => d.toISOString().slice(0, 10);

export const InventoryTab = ({ vials, protocols, peptides = [], refresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [peptide, setPeptide] = useState('');
  const [customName, setCustomName] = useState('');
  const [size, setSize] = useState('');
  const [unit, setUnit] = useState('mg');
  const [started, setStarted] = useState(toDateInput(new Date()));
  const [busy, setBusy] = useState(false);
  const activeProtocols = protocols.filter(p => p.active);
  const NON_INJECTABLE = /case|serum|combo|syringe|bac water|bacteriostatic|acetic/i;
  const protocolNames = new Set(activeProtocols.map(p => p.peptide_name.toLowerCase()));
  const catalog = peptides.filter(p => p.category !== 'Accessories' && p.category !== 'Skincare' && !NON_INJECTABLE.test(p.name) && !protocolNames.has(p.name.toLowerCase()));

  const save = async () => {
    const name = peptide === '__custom__' ? customName.trim() : peptide;
    if (!name || !size || parseFloat(size) <= 0) {
      toast.error('Pick a peptide and enter the vial size.');
      return;
    }
    setBusy(true);
    try {
      await memberApi.post('/member/vials', { peptide_name: name, size_value: parseFloat(size), size_unit: unit, started_at: started ? `${started}T00:00:00` : '' });
      toast.success('Vial added — tracking started!');
      setShowForm(false);
      setPeptide(''); setCustomName(''); setSize('');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add vial.');
    } finally {
      setBusy(false);
    }
  };

  const finish = async (v) => {
    try {
      await memberApi.patch(`/member/vials/${v.vial_id}/finish`);
      toast.success('Vial marked empty.');
      refresh();
    } catch { toast.error('Failed to update.'); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete the ${v.peptide_name} vial?`)) return;
    try {
      await memberApi.delete(`/member/vials/${v.vial_id}`);
      refresh();
    } catch { toast.error('Failed to delete.'); }
  };

  return (
    <div data-testid="inventory-tab">
      <div className="flex items-center justify-between mb-4">
        <p className="text-gray-400 text-sm">Track what's left in each vial. Doses you log are subtracted automatically.</p>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-1.5 bg-[#C41E3A] hover:bg-[#A01830] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm transition-colors flex-shrink-0" data-testid="add-vial-btn">
          <Plus size={14} /> Add Vial
        </button>
      </div>

      {showForm && (
        <div className="bg-[#0A0A0A] border border-[#D4AF37]/50 rounded-sm p-5 mb-5 space-y-3" data-testid="vial-form">
          <select value={peptide} onChange={e => setPeptide(e.target.value)} className={`${inputCls} cursor-pointer`} data-testid="vial-peptide-select">
            <option value="">Select peptide...</option>
            {activeProtocols.length > 0 && (
              <optgroup label="Your Protocols">
                {activeProtocols.map(p => <option key={p.protocol_id} value={p.peptide_name}>{p.peptide_name}</option>)}
              </optgroup>
            )}
            <optgroup label="All Peptides">
              {catalog.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
            </optgroup>
            <option value="__custom__">Other (type it in)</option>
          </select>
          {peptide === '__custom__' && (
            <input value={customName} onChange={e => setCustomName(e.target.value)} placeholder="Peptide name" className={inputCls} data-testid="vial-custom-name" />
          )}
          <div className="grid grid-cols-3 gap-3">
            <input type="number" min="0" step="any" value={size} onChange={e => setSize(e.target.value)} placeholder="Vial size" className={inputCls} data-testid="vial-size-input" />
            <select value={unit} onChange={e => setUnit(e.target.value)} className={`${inputCls} cursor-pointer`} data-testid="vial-unit-select">
              <option value="mg">mg</option>
              <option value="mcg">mcg</option>
              <option value="iu">IU</option>
            </select>
            <input type="date" value={started} onChange={e => setStarted(e.target.value)} className={inputCls} data-testid="vial-start-input" />
          </div>
          <p className="text-gray-600 text-xs">Doses logged from the start date count against this vial. Adding a new vial of the same peptide retires the old one.</p>
          <button onClick={save} disabled={busy} className="w-full bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 text-white font-bold uppercase tracking-wider py-3 rounded-sm transition-colors" data-testid="vial-save-btn">
            {busy ? 'Saving...' : 'Start Tracking'}
          </button>
        </div>
      )}

      {vials.length === 0 && !showForm ? (
        <p className="text-gray-500 text-sm text-center py-8" data-testid="no-vials">
          <Package size={32} className="mx-auto mb-3 text-gray-600" />
          No vials tracked yet — add one when you start a new vial.
        </p>
      ) : (
        <div className="space-y-3">
          {vials.map(v => (
            <div key={v.vial_id} className={`bg-[#0A0A0A] border rounded-sm p-4 ${!v.active ? 'opacity-50 border-[#27272A]' : v.low ? 'border-[#C41E3A]' : 'border-[#27272A]'}`} data-testid={`vial-${v.vial_id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <p className="text-white text-sm font-semibold">
                    {v.peptide_name} <span className="text-gray-500 font-normal">— {v.size_value}{v.size_unit} vial</span>
                    {!v.active && <span className="text-gray-600 text-[10px] uppercase tracking-wider ml-2">Finished</span>}
                  </p>
                  <p className="text-gray-400 text-xs mt-1" data-testid={`vial-remaining-${v.vial_id}`}>
                    {v.remaining_value}{v.remaining_unit} remaining
                    {v.days_remaining !== null && v.active && <span className={v.low ? 'text-[#C41E3A] font-bold' : 'text-[#D4AF37]'}> · ~{v.days_remaining} days left</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {v.active && (
                    <button onClick={() => finish(v)} className="text-gray-400 hover:text-[#D4AF37] transition-colors" title="Mark empty" data-testid={`vial-finish-${v.vial_id}`}>
                      <CheckSquare size={16} />
                    </button>
                  )}
                  <button onClick={() => remove(v)} className="text-gray-400 hover:text-[#C41E3A] transition-colors" data-testid={`vial-delete-${v.vial_id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="h-2 bg-[#1a1a1a] rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${v.percent <= 20 ? 'bg-[#C41E3A]' : v.percent <= 50 ? 'bg-[#D4AF37]' : 'bg-green-600'}`} style={{ width: `${v.percent}%` }} />
              </div>
              {v.low && v.active && (
                <div className="flex items-center justify-between mt-3 bg-[#C41E3A]/10 border border-[#C41E3A]/40 rounded-sm px-3 py-2" data-testid={`vial-low-${v.vial_id}`}>
                  <span className="flex items-center gap-1.5 text-[#C41E3A] text-xs font-bold uppercase"><AlertTriangle size={13} /> Running Low</span>
                  <Link to={v.product_id ? `/product/${v.product_id}` : '/#products'} className="bg-[#C41E3A] hover:bg-[#A01830] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm transition-colors" data-testid={`vial-reorder-${v.vial_id}`}>
                    Reorder
                  </Link>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
